import Pod from "components/Pod/Pod";
import "./AiKnowledgeBase.scss";
import { useEffect, useCallback, useRef, useState } from "react";
import DataTable from "components/DataTable/DataTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faTrashCan, faCircleInfo, faXmark } from "@fortawesome/free-solid-svg-icons";
import { ModalButtons } from "components/Modal/ModalButtons";
import MessageModal from "components/Modal/MessageModal";
import { Button, ButtonType } from "components/Button/Button";
import { Toast } from "primereact/toast";
import { getFetchOptions } from "utils/fetch";
import { Message } from "primereact/message";
import { DataFetcher } from "components/DataFetcher";

const API_BASE = "/api/v3/AiKnowledgeBase";

// DataFetcher request contexts.
const RequestContext = Object.freeze({
	GetFiles: "GetFiles",
	GetOffers: "GetOffers",
	PostFile: "PostFile",
	DeleteFile: "DeleteFile",
});

const AiKnowledgeBase = () => {
	const inputRef = useRef(null);
	const [fileToDelete, setFileToDelete] = useState(null);
	const toast = useRef(null);
	const [pendingFile, setPendingFile] = useState(null);
	const [selectedOfferCode, setSelectedOfferCode] = useState("");
	const [rows, setRows] = useState([]);       // documents for the selected offer
	const [offers, setOffers] = useState([]);   // { code, name }
	const [fetchData, setFetchData] = useState({ url: `${API_BASE}/Offers`, context: RequestContext.GetOffers });

	const setFetchRequestData = useCallback(({ url, options = null, context = null }) => {
		setFetchData({ url, options, context });
	}, []);

	const handleData = useCallback((fetchedData, token, context) => {
		if (!fetchedData) {
			return;
		}

		switch (context) {
			case RequestContext.GetFiles:
				setRows(fetchedData);
				break;
			case RequestContext.GetOffers:
				setOffers(fetchedData);
				break;
			case RequestContext.PostFile:
				setRows((prev) => [...prev.filter((row) => row.FileName.toLowerCase() !== fetchedData.FileName.toLowerCase()), fetchedData]);
				toast.current.show({
					severity: "success",
					summary: "File uploaded successfully",
					detail: "AI may take several minutes to update.",
					life: 6000,
				});
				break;
			case RequestContext.DeleteFile:
				setRows((prev) => prev.filter((row) => row.FileName.toLowerCase() !== fetchedData.FileName.toLowerCase()));
				break;
			default:
				break;
		}
	}, []);

	useEffect(() => {
		if (!selectedOfferCode) {
			setRows([]);
			return;
		}

		setFetchRequestData({ url: `${API_BASE}/Documents?offerCode=${encodeURIComponent(selectedOfferCode)}`, context: RequestContext.GetFiles });
	}, [selectedOfferCode, setFetchRequestData]);

	const showError = useCallback((summary) => {
		toast.current.show({
			severity: "error",
			summary,
			detail: "Please try again.",
			life: 6000
		});
	}, []);

	const handleError = useCallback((error, context) => {
		switch (context) {
			case RequestContext.GetOffers:
				showError("Could not load offers");
				break;
			case RequestContext.GetFiles:
				showError("Could not load files");
				break;
			case RequestContext.DeleteFile:
				showError("Could not delete file");
				break;
			case RequestContext.PostFile: {
				let detail = "The file could not be uploaded.";
				if (error?.Message === "ERR_INVALID_FILE_TYPE") {
					detail = "Unsupported file type. Upload a .txt, .pdf or .pptx file.";
				} else if (error?.Message === "ERR_FILE_TOO_LARGE") {
					detail = "The file is too large. The maximum upload size is 25 MB.";
				}
				toast.current.show({
					severity: "error",
					summary: "Upload failed",
					detail,
					life: 6000
				});
				break;
			}
			default:
				break;
		}
	}, [showError]);

	const columns = [
		{
			field: "fileName",
			headerName: "File Name",
			valueGetter: (row) => row.FileName,
			style: { maxWidth: "200px", whiteSpace: "normal", wordBreak: "break-word" }
		},
		{
			field: "uploadTime",
			headerName: "Upload Time",
			valueGetter: (row) => row.UploadTime,
			style: { maxWidth: "300px", whiteSpace: "normal", wordBreak: "break-word" }
		},
		{
			field: "uploadBy",
			headerName: "Uploaded By",
			valueGetter: (row) => row.UploadBy,
			style: { maxWidth: "300px", whiteSpace: "normal", wordBreak: "break-word" }
		},
		{
			field: "Delete",
			headerName: "Delete File",
			valueGetter: (row) => (
				<button
					type="button"
					className="ai-knowledge-base-delete-button"
					aria-label={`Delete ${row.FileName}`}
					title="Delete file"
					onClick={() => onDelete(row)}
				>
					<FontAwesomeIcon icon={faTrashCan} />
				</button>
			),
			style: { maxWidth: "300px", whiteSpace: "normal", wordBreak: "break-word" }
		},
	];

	function onDelete(row) {
		setFileToDelete(row);
	}

	function confirmDelete() {
		setFetchRequestData({
			url: `${API_BASE}/Documents?offerCode=${encodeURIComponent(selectedOfferCode)}&fileName=${encodeURIComponent(fileToDelete.FileName)}`,
			options: getFetchOptions({ method: "DELETE" }),
			context: RequestContext.DeleteFile
		});
		setFileToDelete(null);
	}

	function confirmReplace() {
		addFile(pendingFile);
		setPendingFile(null);
	}

	function openFileInput() {
		inputRef.current.click();
	}

	function fileSelected(e) {
		const file = e.target.files[0];
		if (!file) {
			return;
		}

		const isDuplicate = rows.some((row) => row.FileName.toLowerCase() === file.name.toLowerCase());
		if (isDuplicate) {
			setPendingFile(file);
		}
		else {
			addFile(file);
		}
		e.target.value = null;
	}

	function addFile(file) {
		const form = new FormData();
		form.append("file", file);
		form.append("offerCode", selectedOfferCode);

		setFetchRequestData({
			url: `${API_BASE}/Documents`,
			options: getFetchOptions({ method: "POST", contentType: null, body: form }),
			context: RequestContext.PostFile
		});
	}

	return (
		<>
			<DataFetcher fetchData={fetchData} setData={handleData} setError={handleError} />
			<div className="ai-knowledge-base gutters">
				<Pod title="AI Knowledge Base">
					<div className="ai-knowledge-base-item">
						<p>The AI Summary on ucSKILLS and the Digital Assistant draw from a knowledge base generated from our learning content. This page allows you to address gaps in that content by providing additional files to add to the knowledge base for each product. They can be text (.txt), PDF (.pdf) or PowerPoint (.pptx) files and should contain only knowledge which the AI can use to answer user questions. If these have been generated from a transcript of a call, for example, then consider cleaning up the transcript to remove anything extraneous. If you are adding a new file to replace old knowledge then delete the old files so there is no conflicting information.</p>
						<div>
							<Message
								severity="warn"
								text="Warning: Any content uploaded will be available to the AI when answering questions to the users so make sure you are uploading the correct file. It may several minutes for the AI to start using the newly uploaded knowledge."
							/>
						</div>
					</div>
					<div className="ai-knowledge-base-item">
						<h2>Files</h2>
						<form onSubmit={(e) => e.preventDefault()}>
							<div className="ai-knowledge-base-offer">
								<div>
									<label>Offer</label>
									<select
										name="offerCode"
										value={selectedOfferCode}
										onChange={(e) => setSelectedOfferCode(e.target.value)}
									>
										<option value="" disabled>select offer</option>
										{offers.map((offer) => (
											<option key={offer.Code} value={offer.Code}>{offer.Name}</option>
										))}
									</select>
								</div>
								<div className="ai-knowledge-base-upload-row">
									<Button type={ButtonType.Primary} text="Upload new file" disabled={selectedOfferCode===""} icon={faUpload} onClick={openFileInput} />
									<input
										ref={inputRef}
										type="file"
										style={{ display: "none" }}
										accept=".txt,.pdf,.pptx"
										onChange={fileSelected}
									/>
								</div>
							</div>
							<p className="ai-knowledge-base-upload-text">Supported file types: Text (.txt), PDF (.pdf), PowerPoint (.pptx)</p>
						</form>

						<DataTable
							rows={rows}
							columns={columns}
							rowsPerPage={100}
							useCardView={true}
							noSearchOnMobile={true}
							sortField="Name"
							sortOrder={1}
							emptyMessage={
								<div className="ai-knowledge-base-empty">
									<FontAwesomeIcon icon={faCircleInfo} />
									<p>No knowledge base files have been uploaded for this offer.<br />Upload a .txt, .pdf or .pptx file to add information to the AI knowledge base.</p>
								</div>
							}
						/>
						{fileToDelete && (
							<MessageModal
								modalTitle={`Delete ${fileToDelete.FileName}?`}
								modalContent={<p className="ai-knowledge-base-modal-message">Deleting this file will remove its information from the AI knowledge base.</p>}
								modalButtons={ModalButtons.Custom}
								customModalButtonContent={
									<>
										<Button type={ButtonType.Secondary} text="Cancel" icon={faXmark} onClick={() => setFileToDelete(null)} />
										<Button type={ButtonType.Primary} text="Delete File" icon={faTrashCan} onClick={confirmDelete} />
									</>
								}
								closeModal={() => setFileToDelete(null)}
							/>
						)}
						{pendingFile && (
							<MessageModal
								modalTitle={`Replace ${pendingFile.name}?`}
								modalContent={
									<p>A file named <strong>{pendingFile.name}</strong> already exists for this offer.
										Replacing it will delete the existing file and upload this one in its place.</p>
								}
								modalButtons={ModalButtons.Custom}
								customModalButtonContent={
									<>
										<Button type={ButtonType.Secondary} text="Cancel" icon={faXmark} onClick={() => setPendingFile(null)} />
										<Button type={ButtonType.Primary} text="Replace File" icon={faUpload} onClick={confirmReplace} />
									</>
								}
								closeModal={() => setPendingFile(null)}
							/>
						)}
					</div>
				</Pod>
				<Toast ref={toast} />
			</div>
		</>
	);
};

export default AiKnowledgeBase;